import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Target, ShoppingCart, Banknote } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { DateRange, OverviewStats, DailyTrendRow, PlatformBreakdown, CampaignRow } from '../../../api/marketing';
import { fetchOverview, fetchCampaigns } from '../../../api/marketing';
import DateRangeSelector, { daysAgo } from './shared/DateRangeSelector';
import StatCard from './shared/StatCard';
import PlatformBadge from './shared/PlatformBadge';

function formatCurrency(val: number): string {
  return val >= 1000 ? `$${(val / 1000).toFixed(1)}k` : `$${val.toFixed(0)}`;
}

function pctChange(current: number, prior: number): number | null {
  if (prior === 0) return null;
  return ((current - prior) / prior) * 100;
}

export default function AdPerformanceOverview() {
  const [dateRange, setDateRange] = useState<DateRange>(daysAgo(30));
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [dailyTrend, setDailyTrend] = useState<DailyTrendRow[]>([]);
  const [platformBreakdown, setPlatformBreakdown] = useState<PlatformBreakdown[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [sortCol, setSortCol] = useState<string>('totalSpend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchOverview(dateRange),
      fetchCampaigns(dateRange, platformFilter || undefined, 200),
    ])
      .then(([overviewRes, campaignsRes]) => {
        setStats(overviewRes.stats);
        setDailyTrend(overviewRes.dailyTrend);
        setPlatformBreakdown(overviewRes.platformBreakdown);
        setCampaigns(campaignsRes.campaigns);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [dateRange, platformFilter]);

  // Build chart data from daily trend — aggregate by date, split by platform
  const chartData = dailyTrend.reduce<Record<string, { date: string; googleSpend: number; metaSpend: number; totalSpend: number; roas: number; conversionValue: number }>>((acc, row) => {
    if (!acc[row.date]) {
      acc[row.date] = { date: row.date, googleSpend: 0, metaSpend: 0, totalSpend: 0, roas: 0, conversionValue: 0 };
    }
    const entry = acc[row.date];
    if (row.platform === 'google_ads') entry.googleSpend += row.spend;
    else if (row.platform === 'meta_ads') entry.metaSpend += row.spend;
    entry.totalSpend += row.spend;
    entry.conversionValue += row.conversionValue;
    return acc;
  }, {});

  const chartDataArr = Object.values(chartData)
    .map((d) => ({ ...d, roas: d.totalSpend > 0 ? d.conversionValue / d.totalSpend : 0 }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // Deduplicate campaigns by ID for the table
  const campaignMap = new Map<string, CampaignRow & { totalSpend: number; totalConversions: number; totalConversionValue: number; totalImpressions: number; totalClicks: number }>();
  for (const c of campaigns) {
    const key = `${c.platform}_${c.campaignId}`;
    const existing = campaignMap.get(key);
    if (existing) {
      existing.totalSpend += c.spend;
      existing.totalConversions += c.conversions;
      existing.totalConversionValue += c.conversionValue;
      existing.totalImpressions += c.impressions;
      existing.totalClicks += c.clicks;
    } else {
      campaignMap.set(key, {
        ...c,
        totalSpend: c.spend,
        totalConversions: c.conversions,
        totalConversionValue: c.conversionValue,
        totalImpressions: c.impressions,
        totalClicks: c.clicks,
      });
    }
  }

  const sortedCampaigns = Array.from(campaignMap.values())
    .sort((a, b) => {
      const aVal = (a as unknown as Record<string, number>)[sortCol] ?? 0;
      const bVal = (b as unknown as Record<string, number>)[sortCol] ?? 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    })
    .slice(0, 20);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Ad Performance</h2>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-5 gap-4">
          <StatCard
            label="Total Spend"
            value={formatCurrency(stats.totalSpend)}
            change={pctChange(stats.totalSpend, stats.priorPeriod.totalSpend)}
            changeLabel="vs prior"
            icon={DollarSign}
            iconColor="text-water-600"
          />
          <StatCard
            label="Revenue"
            value={formatCurrency(stats.totalConversionValue)}
            icon={Banknote}
            iconColor="text-green-600"
          />
          <StatCard
            label="ROAS"
            value={stats.roas ? `${stats.roas.toFixed(1)}x` : '--'}
            change={stats.roas && stats.priorPeriod.roas ? pctChange(stats.roas, stats.priorPeriod.roas) : null}
            changeLabel="vs prior"
            icon={TrendingUp}
            iconColor="text-moss-600"
          />
          <StatCard
            label="CPA"
            value={stats.cpa ? formatCurrency(stats.cpa) : '--'}
            change={stats.cpa && stats.priorPeriod.cpa ? pctChange(stats.priorPeriod.cpa, stats.cpa) : null}
            changeLabel="vs prior"
            icon={Target}
            iconColor="text-amber-600"
          />
          <StatCard
            label="Conversions"
            value={stats.totalConversions.toFixed(0)}
            change={pctChange(stats.totalConversions, stats.priorPeriod.conversions)}
            changeLabel="vs prior"
            icon={ShoppingCart}
            iconColor="text-clay-600"
          />
        </div>
      ) : null}

      {/* Platform Breakdown */}
      {!loading && platformBreakdown.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {platformBreakdown.map((p) => (
            <div key={p.platform} className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
              <div className="flex items-center justify-between mb-3">
                <PlatformBadge platform={p.platform} />
                <span className={`text-sm font-semibold ${
                  p.roas !== null && p.roas >= 3 ? 'text-green-600' : p.roas !== null && p.roas >= 1 ? 'text-amber-600' : 'text-red-500'
                }`}>
                  {p.roas ? `${p.roas.toFixed(1)}x ROAS` : '--'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-stone-500 dark:text-stone-400">Spend</p>
                  <p className="text-base font-bold text-bark-900 dark:text-stone-100">{formatCurrency(p.spend)}</p>
                </div>
                <div>
                  <p className="text-stone-500 dark:text-stone-400">Revenue</p>
                  <p className="text-base font-bold text-bark-900 dark:text-stone-100">{formatCurrency(p.conversionValue)}</p>
                </div>
                <div>
                  <p className="text-stone-500 dark:text-stone-400">Conversions</p>
                  <p className="text-base font-bold text-bark-900 dark:text-stone-100">{p.conversions.toFixed(0)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trend Chart — Spend by Platform + ROAS */}
      {chartDataArr.length > 0 && (
        <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-4">Daily Spend by Platform + ROAS</h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartDataArr}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-stone-200, #e5e5e5)" />
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => d.slice(5)}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                yAxisId="spend"
                tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                yAxisId="roas"
                orientation="right"
                tickFormatter={(v: number) => `${v.toFixed(1)}x`}
                tick={{ fontSize: 11 }}
              />
              <Tooltip formatter={((value: number, name: string) => [
                name.includes('Spend') ? formatCurrency(value) : `${value.toFixed(2)}x`,
                name,
              ]) as any} />
              <Legend />
              <Bar yAxisId="spend" dataKey="googleSpend" name="Google Spend" stackId="spend" fill="#3B82F6" opacity={0.8} />
              <Bar yAxisId="spend" dataKey="metaSpend" name="Meta Spend" stackId="spend" fill="#6366F1" opacity={0.8} radius={[2, 2, 0, 0]} />
              <Line yAxisId="roas" dataKey="roas" name="ROAS" stroke="#B8860B" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Campaign Table */}
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 dark:border-stone-700">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">Campaigns</h3>
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="text-xs border border-stone-300 dark:border-stone-600 rounded-md px-2 py-1 bg-white dark:bg-[#1a1f1c] text-bark-900 dark:text-stone-200"
          >
            <option value="">All Platforms</option>
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-[#1e2520]">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Campaign</th>
                <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Platform</th>
                {[
                  { col: 'totalSpend', label: 'Spend' },
                  { col: 'totalImpressions', label: 'Impr.' },
                  { col: 'totalClicks', label: 'Clicks' },
                  { col: 'totalConversions', label: 'Conv.' },
                  { col: 'totalConversionValue', label: 'Revenue' },
                ].map(({ col, label }) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400 cursor-pointer hover:text-bark-900 dark:hover:text-stone-100"
                  >
                    {label}
                    {sortCol === col && (sortDir === 'desc' ? ' \u2193' : ' \u2191')}
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">ROAS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="w-6 h-6 border-2 border-moss-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : sortedCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-stone-400">
                    No campaign data for this period
                  </td>
                </tr>
              ) : (
                sortedCampaigns.map((c) => {
                  const roas = c.totalSpend > 0 ? c.totalConversionValue / c.totalSpend : null;
                  return (
                    <tr key={`${c.platform}_${c.campaignId}`} className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
                      <td className="px-4 py-3 font-medium text-bark-900 dark:text-stone-200 max-w-[200px] truncate">
                        {c.campaignName}
                      </td>
                      <td className="px-4 py-3"><PlatformBadge platform={c.platform} /></td>
                      <td className="px-4 py-3 text-right">{formatCurrency(c.totalSpend)}</td>
                      <td className="px-4 py-3 text-right">{c.totalImpressions.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">{c.totalClicks.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">{c.totalConversions.toFixed(0)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(c.totalConversionValue)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${
                        roas !== null && roas >= 3 ? 'text-green-600' : roas !== null && roas >= 1 ? 'text-amber-600' : 'text-red-500'
                      }`}>
                        {roas !== null ? `${roas.toFixed(1)}x` : '--'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
